import { useEffect, useMemo, useState } from "react";
import BillingRulesPanel from "./BillingRulesPanel";

const API_URL = "/api/service_users.php";
export const dateInJapan = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:"Asia/Tokyo", year:"numeric", month:"2-digit", day:"2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part=>[part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};
const emptyForm = () => ({
  name: "",
  name_kana: "",
  gender: "",
  birth_date: "",
  room_number: "",
  address: "",
  phone: "",
  emergency_contact_name: "",
  emergency_contact_relationship: "",
  emergency_contact_phone: "",
  payment_method: "",
  started_date: dateInJapan(),
  building_id: "",
  building_started_date: dateInJapan(),
  status: "active",
});

async function request(path = "", method = "GET", body = null) {
  const options = {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  };
  if (body) options.body = JSON.stringify(body);
  const response = await fetch(`${API_URL}${path}`, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "利用者情報の取得に失敗しました");
  return data;
}

async function requestBuildings(storeId) {
  const response = await fetch(`/api/service_buildings.php?storeId=${encodeURIComponent(storeId)}`, {
    method:"GET", credentials:"include", headers:{ "Content-Type":"application/json" },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "建物一覧の取得に失敗しました");
  return data;
}

const fieldStyle = {
  width: "100%", boxSizing: "border-box", border: "1px solid #ddd", borderRadius: 7,
  padding: "9px 10px", fontSize: 14, background: "#fff",
};

const requirementStyle = required => ({
  marginLeft:6, fontSize:10, fontWeight:700,
  color:required ? "#b91c1c" : "#777",
});

function Requirement({ required }) {
  return <span style={requirementStyle(required)}>{required ? "必須" : "任意"}</span>;
}

const fieldDefinitions = [
  ["name", "氏名", "text", true],
  ["name_kana", "ふりがな", "text"],
  ["gender", "性別", "text"],
  ["birth_date", "生年月日", "date"],
  ["room_number", "部屋番号", "text"],
  ["address", "住所", "text"],
  ["phone", "電話番号", "tel"],
  ["emergency_contact_name", "緊急連絡先氏名", "text"],
  ["emergency_contact_relationship", "緊急連絡先続柄", "text"],
  ["emergency_contact_phone", "緊急連絡先電話", "tel"],
  ["payment_method", "支払方法", "text"],
  ["started_date", "利用開始日", "date", true],
];

export const buildServiceUserPayload = (form, id, storeId) => ({
  ...Object.fromEntries(fieldDefinitions.map(([key])=>[key, form[key] ?? ""])),
  status: form.status || "active",
  ...(id ? { id } : {}),
  storeId,
  ...(!id && form.building_id ? {
    buildingId:form.building_id,
    buildingStartedDate:form.building_started_date || form.started_date,
  } : {}),
});

function ServiceUserForm({ initial, targetFacility, buildings = [], onCancel, onSaved }) {
  const [form, setForm] = useState(() => initial ? {
    ...emptyForm(),
    ...Object.fromEntries(Object.entries(initial).map(([key, value]) => [key, value ?? ""])),
  } : emptyForm());
  const targetStoreId = initial?.store_id || targetFacility?.id || "";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const creating = !initial;

  const save = async event => {
    event.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = buildServiceUserPayload(form, initial?.id, targetStoreId);
      const data = await request("", creating ? "POST" : "PUT", payload);
      onSaved(data.service_user);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return <form onSubmit={save} style={{ background:"#f8fafc", border:"1px solid #ddd", borderRadius:12, padding:18, marginBottom:18 }}>
    <div style={{ fontWeight:800, marginBottom:14 }}>{creating ? `${targetFacility?.name || "選択した施設"}に利用者を新規登録` : "利用者を編集"}</div>
    {creating && <div aria-label="登録先施設" style={{ background:"#ecfdf5", border:"1px solid #86efac", borderRadius:8, padding:"10px 12px", marginBottom:12, color:"#166534", fontWeight:800 }}>
      登録先施設：{targetFacility?.name}
      <div style={{ fontSize:11, fontWeight:400, marginTop:3 }}>変更する場合はキャンセルし、一覧画面で施設を選び直してください。</div>
    </div>}
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:12 }}>
      {fieldDefinitions.map(([key, label, type, required])=><label key={key} style={{ fontSize:12, color:"#555" }}>{label}<Requirement required={Boolean(required)} />
        <input
          aria-label={label}
          type={type}
          value={form[key]}
          required={required}
          onChange={event=>setForm(current=>({ ...current, [key]:event.target.value }))}
          style={fieldStyle}
        />
      </label>)}
      {creating && <>
        <label style={{ fontSize:12, color:"#555" }}>建物<Requirement required={false} />
          <select aria-label="建物" value={form.building_id} onChange={event=>setForm(current=>({ ...current, building_id:event.target.value }))} style={fieldStyle}>
            <option value="">建物未設定</option>
            {buildings.map(building=><option key={building.id} value={building.id}>{building.name}</option>)}
          </select>
        </label>
        {form.building_id && <label style={{ fontSize:12, color:"#555" }}>建物所属開始日<Requirement required />
          <input aria-label="建物所属開始日" type="date" value={form.building_started_date || form.started_date} min={form.started_date} required onChange={event=>setForm(current=>({ ...current, building_started_date:event.target.value }))} style={fieldStyle}/>
        </label>}
      </>}
      {!creating && <label style={{ fontSize:12, color:"#555" }}>利用状態
        <select aria-label="利用状態" value={form.status} onChange={event=>setForm(current=>({ ...current, status:event.target.value }))} style={fieldStyle}>
          <option value="active">利用中</option>
          <option value="inactive">休止中</option>
        </select>
      </label>}
    </div>
    {error && <div role="alert" style={{ color:"#b91c1c", marginTop:10 }}>{error}</div>}
    <div style={{ display:"flex", gap:8, marginTop:14 }}>
      <button type="submit" disabled={saving} style={{ background:"#2563eb", color:"#fff", border:0, borderRadius:7, padding:"9px 18px", fontWeight:700 }}>{saving ? "保存中…" : "保存"}</button>
      <button type="button" onClick={onCancel} style={{ background:"#fff", border:"1px solid #ccc", borderRadius:7, padding:"9px 18px" }}>キャンセル</button>
    </div>
  </form>;
}

function EndForm({ serviceUser, onCancel, onEnded }) {
  const [endedDate, setEndedDate] = useState(dateInJapan());
  const [endedReason, setEndedReason] = useState("");
  const [error, setError] = useState("");
  const submit = async event => {
    event.preventDefault();
    setError("");
    try {
      const data = await request("", "PUT", {
        id: serviceUser.id, action:"end", ended_date:endedDate, ended_reason:endedReason,
      });
      onEnded(data.service_user);
    } catch (requestError) {
      setError(requestError.message);
    }
  };
  return <form onSubmit={submit} style={{ background:"#fff7ed", border:"1px solid #fdba74", borderRadius:10, padding:14, marginTop:10 }}>
    <div style={{ fontWeight:700, marginBottom:8 }}>{serviceUser.name}さんの利用終了</div>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:8 }}>
      <label style={{ fontSize:12, color:"#555" }}>利用終了日<Requirement required />
        <input aria-label="利用終了日" type="date" value={endedDate} min={serviceUser.started_date} onChange={event=>setEndedDate(event.target.value)} required style={fieldStyle}/>
      </label>
      <label style={{ fontSize:12, color:"#555" }}>利用終了理由<Requirement required={false} />
        <input aria-label="利用終了理由" value={endedReason} onChange={event=>setEndedReason(event.target.value)} placeholder="退所、契約終了など" style={fieldStyle}/>
      </label>
    </div>
    {error && <div role="alert" style={{ color:"#b91c1c", marginTop:8 }}>{error}</div>}
    <div style={{ display:"flex", gap:8, marginTop:10 }}>
      <button type="submit">利用終了を確定</button><button type="button" onClick={onCancel}>キャンセル</button>
    </div>
  </form>;
}

export default function ServiceUsersTab({ store }) {
  const [facilities, setFacilities] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [serviceUsers, setServiceUsers] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null);
  const [ending, setEnding] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    request("?action=targets").then(data => {
      if (!active) return;
      const targets = data.facilities || [];
      setFacilities(targets);
      setSelectedStoreId(current => {
        if (targets.length === 1) return targets[0].id;
        return targets.some(target=>target.id === current) ? current : "";
      });
    }).catch(requestError=>active && setError(requestError.message));
    return () => { active = false; };
  }, [store.id]);

  useEffect(() => {
    setBuildings([]);
    if (!selectedStoreId) { setLoading(false); return; }
    let active = true;
    setLoading(true);
    Promise.all([
      request(`?storeId=${encodeURIComponent(selectedStoreId)}&status=${encodeURIComponent(status)}`),
      requestBuildings(selectedStoreId),
    ]).then(([data, buildingData]) => {
        if (!active) return;
        setServiceUsers(data.service_users || []);
        setBuildings(buildingData.buildings || []);
        setError("");
      })
      .catch(requestError=>active && setError(requestError.message))
      .finally(()=>active && setLoading(false));
    return () => { active = false; };
  }, [selectedStoreId, status]);

  const selectedFacility = useMemo(
    () => facilities.find(facility=>facility.id === selectedStoreId),
    [facilities, selectedStoreId]
  );
  const applySaved = saved => {
    setServiceUsers(current => {
      const found = current.some(item=>item.id === saved.id);
      return found ? current.map(item=>item.id === saved.id ? saved : item) : [saved, ...current];
    });
    setCreating(false); setEditing(null); setEnding(null);
  };
  const deleteServiceUser = async serviceUser => {
    const confirmed = window.confirm(
      "この利用者を完全に削除します。この操作は、誤登録・重複登録・テスト登録の場合のみ使用してください。削除後は元に戻せません。"
    );
    if (!confirmed) return;
    setError("");
    setDeletingId(serviceUser.id);
    try {
      await request("", "DELETE", { id:serviceUser.id });
      setServiceUsers(current=>current.filter(item=>item.id !== serviceUser.id));
      setViewingId(null);
      setEditing(null);
      setEnding(null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (!loading && facilities.length === 0) return <div role="alert">利用者を閲覧できる施設がありません。</div>;

  return <div>
    <div style={{ display:"flex", justifyContent:"space-between", gap:10, alignItems:"center", flexWrap:"wrap", marginBottom:14 }}>
      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
        <label style={{ fontSize:12, color:"#555" }}>対象施設
        <select aria-label="表示施設" value={selectedStoreId} onChange={event=>{ setSelectedStoreId(event.target.value); setCreating(false); setEditing(null); setEnding(null); setViewingId(null); }} style={{ marginLeft:6 }}>
          {facilities.length > 1 && <option value="">登録・表示する施設を選択</option>}
          {facilities.map(facility=><option key={facility.id} value={facility.id}>{facility.name}</option>)}
        </select>
        </label>
        <select aria-label="利用状態フィルター" value={status} onChange={event=>setStatus(event.target.value)}>
          <option value="all">すべて</option><option value="active">利用中</option><option value="inactive">休止中</option><option value="ended">利用終了</option>
        </select>
      </div>
      {selectedFacility?.can_create && <button onClick={()=>{ setCreating(true); setEditing(null); }} style={{ background:store.accent, color:"#fff", border:0, borderRadius:7, padding:"9px 16px", fontWeight:700 }}>{selectedFacility.name}に新規登録</button>}
    </div>
    {selectedFacility ? <div style={{ background:"#f0fdf4", color:"#166534", borderRadius:8, padding:"8px 12px", marginBottom:12, fontSize:13, fontWeight:700 }}>選択中の施設：{selectedFacility.name}</div>
      : <div style={{ background:"#fff7ed", color:"#9a3412", borderRadius:8, padding:"10px 12px", marginBottom:12, fontSize:13 }}>最初に登録・表示する施設を選択してください。</div>}
    {creating && <ServiceUserForm targetFacility={selectedFacility} buildings={buildings} onCancel={()=>setCreating(false)} onSaved={applySaved}/>}
    {editing && <ServiceUserForm initial={editing} targetFacility={selectedFacility} buildings={buildings} onCancel={()=>setEditing(null)} onSaved={applySaved}/>}
    {error && <div role="alert" style={{ color:"#b91c1c", marginBottom:12 }}>{error}</div>}
    {loading ? <div>読み込み中…</div> : <div style={{ display:"grid", gap:9 }}>
      {serviceUsers.map(serviceUser=><div key={serviceUser.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 15px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <div>
            <div style={{ fontWeight:800 }}>{serviceUser.name} <span style={{ fontSize:12, color:"#777" }}>{serviceUser.name_kana}</span></div>
            <div style={{ fontSize:12, color:"#666", marginTop:3 }}>部屋 {serviceUser.room_number || "—"} ・ {serviceUser.started_date}〜{serviceUser.ended_date || ""} ・ {serviceUser.status === "active" ? "利用中" : serviceUser.status === "inactive" ? "休止中" : "利用終了"}</div>
          </div>
          <div style={{ display:"flex", gap:7 }}>
            <button onClick={()=>setViewingId(current=>current === serviceUser.id ? null : serviceUser.id)}>詳細</button>
            {serviceUser.can_edit && <button onClick={()=>{ setEditing(serviceUser); setCreating(false); setEnding(null); }}>編集</button>}
            {serviceUser.can_archive && <button onClick={()=>{ setEnding(serviceUser); setEditing(null); }}>利用終了</button>}
          </div>
        </div>
        {viewingId === serviceUser.id && <>
          <dl style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:8, margin:"12px 0 0", paddingTop:10, borderTop:"1px solid #eee" }}>
            {[
              ["性別", serviceUser.gender], ["生年月日", serviceUser.birth_date], ["住所", serviceUser.address],
              ["電話番号", serviceUser.phone], ["緊急連絡先氏名", serviceUser.emergency_contact_name],
              ["緊急連絡先続柄", serviceUser.emergency_contact_relationship],
              ["緊急連絡先電話", serviceUser.emergency_contact_phone], ["支払方法", serviceUser.payment_method],
              ["利用終了理由", serviceUser.ended_reason],
              ["建物所属", (serviceUser.building_assignments || []).map(assignment=>`${assignment.display_name}（${assignment.started_date}〜${assignment.ended_date || ""}）`).join("、")],
            ].map(([label, value])=><div key={label}><dt style={{ fontSize:11, color:"#888" }}>{label}</dt><dd style={{ margin:0, fontSize:13 }}>{value || "—"}</dd></div>)}
          </dl>
          <BillingRulesPanel serviceUser={serviceUser}/>
          {serviceUser.can_delete && <div style={{ marginTop:14, paddingTop:12, borderTop:"1px solid #f1d1d1", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <span style={{ color:"#7f1d1d", fontSize:12 }}>誤登録・重複登録・テスト登録の場合のみ使用</span>
            <button
              type="button"
              onClick={()=>deleteServiceUser(serviceUser)}
              disabled={deletingId === serviceUser.id}
              style={{ color:"#b91c1c", background:"#fff", border:"1px solid #dc2626", borderRadius:7, padding:"7px 11px", fontWeight:700 }}
            >{deletingId === serviceUser.id ? "削除中…" : "誤登録を削除"}</button>
          </div>}
        </>}
        {ending?.id === serviceUser.id && <EndForm serviceUser={serviceUser} onCancel={()=>setEnding(null)} onEnded={applySaved}/>}
      </div>)}
      {serviceUsers.length === 0 && <div style={{ textAlign:"center", color:"#888", padding:30 }}>該当する利用者はいません。</div>}
    </div>}
  </div>;
}
